import { Injectable } from '@angular/core';

export type Position = {
    x: number;
    y: number;
};

export type ResolvePopupModalOverlap = {
    activeElement: ActiveElementEnum.MODAL | ActiveElementEnum.POPUP;
    modalAutoMove: () => void;
    modalElement: HTMLElement;
    modalMove: (position: Position) => void;
    modalPosition: Position;
    padding?: number;
    popupElement: HTMLElement;
    popupMove: (dx: number, dy: number) => void;
};

export type ResolveModalsOverlap = {
    activeModalElement: HTMLElement;
    activeModalPosition: Position;
    padding?: number;
    passiveModalAutoMove: () => void;
    passiveModalElement: HTMLElement;
    passiveModalMove: (position: Position) => void;
    passiveModalPosition: Position;
};

type PushAwayConfig = {
    avoidRect: DOMRect;
    containerElement: HTMLElement;
    elementAutoMove: () => void;
    elementMove: (position: Position) => void;
    elementPosition: Position;
    elementRect: DOMRect;
    padding: number;
};

export const MAP_ELEMENTS_DEFAULT_PADDING = 6;
const MIN_MOVE = 6;
const MARGIN_MAP_RIGHT = 46;

export enum ActiveElementEnum {
    POPUP = 'popup',
    MODAL = 'modal',
}

@Injectable({
    providedIn: 'root',
})
export class MapElementsPositionService {
    public areOverlapping(sourceRect: DOMRect, targetRect: DOMRect): boolean {
        return !(
            sourceRect.right <= targetRect.left ||
            sourceRect.left >= targetRect.right ||
            sourceRect.bottom <= targetRect.top ||
            sourceRect.top >= targetRect.bottom
        );
    }

    public constrainElementInsideContainer(element: HTMLElement, container: HTMLElement): void {
        const containerRect: DOMRect = this.getElementRect(container);

        const elementWidth: number = element.offsetWidth;
        const elementHeight: number = element.offsetHeight;

        const currentPosition: Position = {
            x: element.offsetLeft,
            y: element.offsetTop,
        };

        const constrained: Position = this.constrainPosition(
            currentPosition,
            containerRect,
            elementWidth,
            elementHeight,
        );

        element.style.left = `${constrained.x}px`;
        element.style.top = `${constrained.y}px`;
    }

    public constrainPosition(
        position: Position,
        containerRect: DOMRect,
        elementWidth: number,
        elementHeight: number,
    ): Position {
        const maxX: number = Math.max(containerRect.width - elementWidth, 0) - MARGIN_MAP_RIGHT;
        const maxY: number = Math.max(containerRect.height - elementHeight, 0);

        return {
            x: Math.min(Math.max(position.x, 0), maxX),
            y: Math.min(Math.max(position.y, 0), maxY),
        };
    }

    public resolveModalsOverlap(modalsOverlap: ResolveModalsOverlap): void {
        const padding: number = modalsOverlap.padding ?? MAP_ELEMENTS_DEFAULT_PADDING;

        const activeContainer: DOMRect = this.getElementRect(modalsOverlap.activeModalElement);
        const passiveContainer: DOMRect = this.getElementRect(modalsOverlap.passiveModalElement);

        if (!this.areOverlapping(activeContainer, passiveContainer)) {
            return;
        }

        const overlap: Position = this.getOverlap(activeContainer, passiveContainer);

        this.pushElementAway(
            {
                avoidRect: activeContainer,
                containerElement: modalsOverlap.passiveModalElement.offsetParent as HTMLElement,
                elementAutoMove: modalsOverlap.passiveModalAutoMove,
                elementMove: modalsOverlap.passiveModalMove,
                elementPosition: modalsOverlap.passiveModalPosition,
                elementRect: passiveContainer,
                padding,
            },
            overlap,
        );
    }

    public resolvePopupModalOverlap(popupModalOverlap: ResolvePopupModalOverlap): void {
        const padding: number = popupModalOverlap.padding ?? MAP_ELEMENTS_DEFAULT_PADDING;

        const popupContainer: DOMRect = this.getElementRect(popupModalOverlap.popupElement);
        const modalContainer: DOMRect = this.getElementRect(popupModalOverlap.modalElement);

        if (!this.areOverlapping(popupContainer, modalContainer)) {
            return;
        }

        const overlap: Position = this.getOverlap(popupContainer, modalContainer);

        if (popupModalOverlap.activeElement === ActiveElementEnum.POPUP) {
            this.pushElementAway(
                {
                    avoidRect: popupContainer,
                    containerElement: popupModalOverlap.modalElement.offsetParent as HTMLElement,
                    elementAutoMove: popupModalOverlap.modalAutoMove,
                    elementMove: popupModalOverlap.modalMove,
                    elementPosition: popupModalOverlap.modalPosition,
                    elementRect: modalContainer,
                    padding,
                },
                overlap,
            );

            return;
        }

        this.pushPopupAway(popupModalOverlap, overlap, padding);
    }

    private buildRectFromPosition(containerRect: DOMRect, position: Position, elementRect: DOMRect): DOMRect {
        return new DOMRect(
            containerRect.left + position.x,
            containerRect.top + position.y,
            elementRect.width,
            elementRect.height,
        );
    }

    private getDeltaCandidates(overlap: Position, padding: number): Position[] {
        return [
            { x: overlap.x + padding, y: 0 },
            { x: -overlap.x - padding, y: 0 },
            { x: 0, y: overlap.y + padding },
            { x: 0, y: -overlap.y - padding },
        ];
    }

    private getElementRect(element: HTMLElement): DOMRect {
        return element.getBoundingClientRect();
    }

    private getMoveCandidates(position: Position, overlap: Position, padding: number): Position[] {
        return [
            { x: position.x + overlap.x + padding, y: position.y },
            { x: position.x - overlap.x - padding, y: position.y },
            { x: position.x, y: position.y + overlap.y + padding },
            { x: position.x, y: position.y - overlap.y - padding },
        ];
    }

    private getOverlap(activeRect: DOMRect, passiveRect: DOMRect): Position {
        return {
            x: Math.min(activeRect.right, passiveRect.right) - Math.max(activeRect.left, passiveRect.left),
            y: Math.min(activeRect.bottom, passiveRect.bottom) - Math.max(activeRect.top, passiveRect.top),
        };
    }

    private isMoveSignificant(from: Position, to: Position): boolean {
        const deltaX: number = Math.abs(to.x - from.x);
        const deltaY: number = Math.abs(to.y - from.y);

        return deltaX >= MIN_MOVE || deltaY >= MIN_MOVE;
    }

    private pushElementAway(pushAwayConfig: PushAwayConfig, overlap: Position): void {
        const containerRect: DOMRect = this.getElementRect(pushAwayConfig.containerElement);
        const candidates: Position[] = this.getMoveCandidates(
            pushAwayConfig.elementPosition,
            overlap,
            pushAwayConfig.padding,
        );

        const constrainedPosition: Position | undefined = candidates
            .map((candidate: Position) => {
                return this.constrainPosition(
                    candidate,
                    containerRect,
                    pushAwayConfig.elementRect.width,
                    pushAwayConfig.elementRect.height,
                );
            })
            .find((position: Position) => {
                if (!this.isMoveSignificant(pushAwayConfig.elementPosition, position)) {
                    return false;
                }

                const nextRect: DOMRect = this.buildRectFromPosition(
                    containerRect,
                    position,
                    pushAwayConfig.elementRect,
                );

                return !this.areOverlapping(pushAwayConfig.avoidRect, nextRect);
            });

        if (!constrainedPosition) {
            return;
        }

        pushAwayConfig.elementAutoMove();
        pushAwayConfig.elementMove(constrainedPosition);
    }

    private pushPopupAway(popupModalOverlap: ResolvePopupModalOverlap, overlap: Position, padding: number): void {
        const candidates: Position[] = this.getDeltaCandidates(overlap, padding);

        for (const candidate of candidates) {
            popupModalOverlap.popupMove(candidate.x, candidate.y);

            const popupContainer: DOMRect = this.getElementRect(popupModalOverlap.popupElement);
            const modalContainer: DOMRect = this.getElementRect(popupModalOverlap.modalElement);

            if (!this.areOverlapping(popupContainer, modalContainer)) {
                return;
            }

            popupModalOverlap.popupMove(-candidate.x, -candidate.y);
        }
    }
}
